/**
 * Platform Inbox — Gmail-style 3-pane view for inbound mail to Savenes
 * mailboxes (info@, support@, sales@…). Visible to roles configured on each
 * mailbox (default: super_admin, practice_admin).
 *
 * Layout:
 *   - Left: mailbox list + filters
 *   - Center: message list (sender, subject, snippet, time, ticket badge)
 *   - Right: selected message + thread + reply/forward
 *
 * Sends reply/forward through Resend (the existing crm_emails infra). The
 * inbound side ingests via POST /api/webhooks/inbound-email (provider-agnostic).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Inbox, Mail, Search, RefreshCw, Loader2, Star, Reply, Forward, Trash2, Archive,
  ArchiveRestore, MailOpen, Tag, Plus, Settings, Send, Paperclip, X, Ticket as TicketIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import RichTextEditor from '../../components/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('savenes_token')}` });

const fmtTime = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const fmtShort = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString();
  } catch { return iso; }
};

export default function PlatformInboxPage() {
  const [mailboxes, setMailboxes] = useState([]);
  const [activeMbId, setActiveMbId] = useState(null);
  const [filter, setFilter] = useState({ unread: false, archived: false, starred: false, search: '' });
  const [messages, setMessages] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // { message, thread }
  const [openMailboxSettings, setOpenMailboxSettings] = useState(false);
  const [openCompose, setOpenCompose] = useState(null); // 'reply' | 'forward' | null

  const loadMailboxes = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/inbox/mailboxes`, { headers: auth() });
      setMailboxes(r.data.mailboxes || []);
    } catch { /* role-based — empty is fine */ }
  };

  const seedDefaults = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/inbox/seed`, {}, { headers: auth() });
      toast.success(`Seeded ${r.data.created?.length || 0} mailboxes (skipped ${r.data.skipped || 0})`);
      loadMailboxes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
  };

  const loadMessages = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeMbId) params.mailbox_id = activeMbId;
      if (filter.unread) params.unread = true;
      if (filter.archived) params.archived = true;
      if (filter.starred) params.starred = true;
      if (filter.search) params.search = filter.search;
      const r = await axios.get(`${API_URL}/api/inbox/messages`, { headers: auth(), params });
      setMessages(r.data.messages || []);
      setUnreadTotal(r.data.unread_total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load inbox');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadMailboxes(); }, []);
  // Auto-refresh inbox every 20s
  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMbId, filter.unread, filter.archived, filter.starred]);

  const openMessage = async (m) => {
    try {
      const r = await axios.get(`${API_URL}/api/inbox/messages/${m.id}`, { headers: auth() });
      setSelected(r.data);
      // Optimistically mark as read in the list
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_read: true } : x)));
      setUnreadTotal((n) => Math.max(0, n - (m.is_read ? 0 : 1)));
    } catch (e) {
      toast.error('Failed to open message');
    }
  };

  const updateMsg = async (id, patch) => {
    try {
      await axios.patch(`${API_URL}/api/inbox/messages/${id}`, patch, { headers: auth() });
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
      if (selected?.message?.id === id) setSelected({ ...selected, message: { ...selected.message, ...patch } });
    } catch { toast.error('Update failed'); }
  };

  const deleteMsg = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API_URL}/api/inbox/messages/${id}`, { headers: auth() });
      setMessages((prev) => prev.filter((m) => m.id !== id));
      if (selected?.message?.id === id) setSelected(null);
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="platform-inbox-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A2342] flex items-center gap-2">
            <Inbox className="w-6 h-6 text-[#0E7490]" /> Platform Inbox
            {unreadTotal > 0 && <span className="text-xs font-medium bg-[#991B1B] text-white rounded-full px-2 py-0.5">{unreadTotal} unread</span>}
          </h1>
          <p className="text-sm text-[#64748B]">Inbound mail to info@, support@, sales@, and other configured mailboxes.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadMessages} disabled={loading} data-testid="inbox-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button variant="outline" onClick={() => setOpenMailboxSettings(true)} data-testid="inbox-mailbox-settings-btn">
            <Settings className="w-4 h-4 mr-1.5" /> Mailboxes
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[220px_minmax(280px,1fr)_1.4fr] gap-4">
        {/* Left: mailboxes + filters */}
        <Card className="careiq-card">
          <CardContent className="p-2">
            {mailboxes.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-xs text-[#94A3B8] mb-3">No mailboxes configured.</p>
                <Button size="sm" className="careiq-btn-primary text-xs" onClick={seedDefaults} data-testid="inbox-seed-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Seed defaults
                </Button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setActiveMbId(null)}
                  className={`w-full text-left px-2.5 py-2 rounded text-xs ${!activeMbId ? 'bg-[#0E7490]/10 text-[#0E7490] font-medium' : 'text-[#0A2342] hover:bg-[#F8FAFC]'}`}
                  data-testid="inbox-mailbox-all"
                >
                  <Mail className="w-3.5 h-3.5 inline mr-1.5" /> All inboxes
                </button>
                {mailboxes.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setActiveMbId(m.id)}
                    className={`w-full text-left px-2.5 py-2 rounded text-xs truncate ${activeMbId === m.id ? 'bg-[#0E7490]/10 text-[#0E7490] font-medium' : 'text-[#0A2342] hover:bg-[#F8FAFC]'}`}
                    data-testid={`inbox-mailbox-${m.id}`}
                    title={m.address}
                  >
                    <Mail className="w-3.5 h-3.5 inline mr-1.5" /> {m.address}
                    {m.auto_ticket_enabled && <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-[#0E7490]" title="Auto-ticket enabled"></span>}
                  </button>
                ))}
              </>
            )}
            <div className="mt-3 pt-3 border-t border-[#F1F5F9] space-y-1">
              <FilterChip label="Unread" active={filter.unread} onClick={() => setFilter({ ...filter, unread: !filter.unread })} testid="filter-unread" />
              <FilterChip label="Starred" active={filter.starred} onClick={() => setFilter({ ...filter, starred: !filter.starred })} testid="filter-starred" />
              <FilterChip label="Archived" active={filter.archived} onClick={() => setFilter({ ...filter, archived: !filter.archived })} testid="filter-archived" />
            </div>
          </CardContent>
        </Card>

        {/* Center: message list */}
        <Card className="careiq-card">
          <CardHeader className="pb-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && loadMessages()}
                placeholder="Search subject / sender…"
                className="pl-8 h-9 text-sm"
                data-testid="inbox-search-input"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-[68vh] overflow-y-auto">
            {loading && messages.length === 0 && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-[#0E7490] animate-spin" /></div>}
            {!loading && messages.length === 0 && (
              <div className="text-center py-12 text-sm text-[#94A3B8]" data-testid="inbox-empty">
                <Inbox className="w-8 h-8 mx-auto mb-2 text-[#CBD5E1]" />
                No messages.
              </div>
            )}
            <ul className="divide-y divide-[#F1F5F9]">
              {messages.map((m) => (
                <li
                  key={m.id}
                  onClick={() => openMessage(m)}
                  className={`px-3 py-2.5 cursor-pointer hover:bg-[#F8FAFC] ${selected?.message?.id === m.id ? 'bg-[#0E7490]/5 border-l-2 border-[#0E7490]' : ''} ${m.is_read ? '' : 'bg-[#FFFBEB]/30'}`}
                  data-testid={`inbox-msg-${m.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${m.is_read ? 'text-[#64748B]' : 'text-[#0A2342] font-semibold'}`}>{m.from_name || m.from_email}</span>
                    <span className="text-[10px] text-[#94A3B8] shrink-0">{fmtShort(m.received_at)}</span>
                  </div>
                  <div className={`text-xs truncate ${m.is_read ? 'text-[#64748B]' : 'text-[#0A2342] font-medium'}`}>{m.subject || '(no subject)'}</div>
                  <div className="text-[11px] text-[#94A3B8] truncate">{m.snippet}</div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#64748B]">{m.mailbox_address}</span>
                    {m.ticket_id && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#7C3AED]/10 text-[#7C3AED]"><TicketIcon className="w-2.5 h-2.5 inline mr-0.5" />Ticket</span>}
                    {m.starred && <Star className="w-3 h-3 text-[#CA8A04]" fill="#CA8A04" />}
                    {m.archived && <Archive className="w-3 h-3 text-[#94A3B8]" />}
                    {(m.attachments || []).length > 0 && <Paperclip className="w-3 h-3 text-[#94A3B8]" />}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Right: selected message + thread */}
        <Card className="careiq-card">
          {selected ? (
            <SelectedMessage
              data={selected}
              onUpdate={(p) => updateMsg(selected.message.id, p)}
              onDelete={() => deleteMsg(selected.message.id)}
              onReply={() => setOpenCompose('reply')}
              onForward={() => setOpenCompose('forward')}
            />
          ) : (
            <CardContent className="flex flex-col items-center justify-center py-20 text-center">
              <Mail className="w-10 h-10 text-[#CBD5E1] mb-3" />
              <p className="text-sm text-[#64748B]">Select a message to view the thread and reply.</p>
            </CardContent>
          )}
        </Card>
      </div>

      <ComposeDialog
        open={!!openCompose}
        mode={openCompose}
        message={selected?.message}
        onClose={() => setOpenCompose(null)}
        onSent={() => { setOpenCompose(null); if (selected) openMessage(selected.message); }}
      />

      <MailboxSettingsDialog
        open={openMailboxSettings}
        onClose={() => { setOpenMailboxSettings(false); loadMailboxes(); }}
        mailboxes={mailboxes}
        onChanged={loadMailboxes}
      />
    </div>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-2.5 py-1.5 rounded text-xs ${active ? 'bg-[#0E7490]/10 text-[#0E7490] font-medium' : 'text-[#64748B] hover:bg-[#F8FAFC]'}`}
      data-testid={testid}
    >
      <Tag className="w-3 h-3 inline mr-1.5" /> {label}
    </button>
  );
}

function SelectedMessage({ data, onUpdate, onDelete, onReply, onForward }) {
  const m = data.message;
  return (
    <>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <CardTitle className="text-lg font-semibold text-[#0A2342] truncate" data-testid="msg-detail-subject">{m.subject || '(no subject)'}</CardTitle>
          <div className="text-xs text-[#64748B] mt-1">
            <span className="font-medium text-[#0A2342]">{m.from_name || m.from_email}</span>
            <span className="text-[#94A3B8]"> &lt;{m.from_email}&gt;</span>
            <span className="mx-1">·</span>
            <span>to {m.delivered_to || m.mailbox_address}</span>
            <span className="mx-1">·</span>
            <span>{fmtTime(m.received_at)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="ghost" onClick={() => onUpdate({ starred: !m.starred })} title="Star" data-testid="msg-star-btn">
            <Star className={`w-4 h-4 ${m.starred ? 'text-[#CA8A04] fill-[#CA8A04]' : 'text-[#94A3B8]'}`} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onUpdate({ is_read: !m.is_read })} title={m.is_read ? 'Mark unread' : 'Mark read'} data-testid="msg-toggle-read-btn">
            <MailOpen className="w-4 h-4 text-[#64748B]" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onUpdate({ archived: !m.archived })} title={m.archived ? 'Unarchive' : 'Archive'} data-testid="msg-archive-btn">
            {m.archived ? <ArchiveRestore className="w-4 h-4 text-[#64748B]" /> : <Archive className="w-4 h-4 text-[#64748B]" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-[#991B1B] hover:bg-[#991B1B]/5" data-testid="msg-delete-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {m.ticket_id && (
          <a href={`/tickets/${m.ticket_id}`} className="inline-flex items-center gap-1.5 text-xs text-[#7C3AED] hover:underline">
            <TicketIcon className="w-3.5 h-3.5" /> Linked support ticket
          </a>
        )}
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={onReply} className="careiq-btn-primary text-xs h-8" data-testid="msg-reply-btn">
            <Reply className="w-3.5 h-3.5 mr-1.5" /> Reply
          </Button>
          <Button size="sm" variant="outline" onClick={onForward} className="text-xs h-8" data-testid="msg-forward-btn">
            <Forward className="w-3.5 h-3.5 mr-1.5" /> Forward
          </Button>
        </div>
        <div
          className="prose prose-sm max-w-none bg-[#F8FAFC] border border-[#E2E8F0] rounded p-3 max-h-[40vh] overflow-y-auto"
          data-testid="msg-body"
          dangerouslySetInnerHTML={{ __html: m.html || `<pre style="white-space:pre-wrap">${m.text || ''}</pre>` }}
        />
        {(m.attachments || []).length > 0 && (
          <div>
            <div className="text-xs font-medium text-[#0A2342] mb-1">Attachments</div>
            <ul className="text-xs space-y-1">
              {m.attachments.map((a, i) => (
                <li key={i} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded px-2 py-1 inline-flex items-center gap-1.5 mr-1.5">
                  <Paperclip className="w-3 h-3 text-[#64748B]" /> {a.filename} <span className="text-[#94A3B8]">· {(a.size / 1024).toFixed(0)} KB</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.thread && data.thread.length > 1 && (
          <div className="border-t border-[#F1F5F9] pt-3">
            <div className="text-xs font-medium text-[#0A2342] mb-2">Thread ({data.thread.length})</div>
            <ul className="space-y-2">
              {data.thread.map((t) => (
                <li key={t.id} className={`border rounded p-2 text-xs ${t.id === m.id ? 'border-[#0E7490]/30 bg-[#0E7490]/5' : 'border-[#E2E8F0]'}`} data-testid={`thread-item-${t.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-[#0A2342] truncate">{t.from_name || t.from_email}</span>
                    <span className="text-[10px] text-[#94A3B8] shrink-0">{fmtShort(t.received_at)}</span>
                  </div>
                  <div className="text-[#64748B] truncate">{t.snippet}</div>
                  {t.direction === 'outbound' && <span className="text-[10px] text-[#0E7490] font-medium">Sent</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </>
  );
}

function ComposeDialog({ open, mode, message, onClose, onSent }) {
  const [html, setHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !message) return;
    setHtml('');
    setCc('');
    setBcc('');
    setAttachments([]);
    if (mode === 'reply') {
      setSubject(message.subject?.startsWith('Re:') ? message.subject : `Re: ${message.subject || ''}`);
      setTo(message.from_email || '');
    } else if (mode === 'forward') {
      setSubject(message.subject?.startsWith('Fwd:') ? message.subject : `Fwd: ${message.subject || ''}`);
      setTo('');
    }
  }, [open, mode, message]);

  const send = async () => {
    if (mode === 'forward' && !to.trim()) { toast.error('Recipient required'); return; }
    if (!html || html === '<p></p>') { toast.error('Body is empty'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('html', html);
      if (subject) fd.append('subject', subject);
      if (mode === 'forward') fd.append('to', to);
      if (cc) fd.append('cc', cc);
      if (bcc) fd.append('bcc', bcc);
      for (const f of attachments) fd.append('attachments', f, f.name);
      const path = mode === 'reply' ? 'reply' : 'forward';
      await axios.post(`${API_URL}/api/inbox/messages/${message.id}/${path}`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      toast.success(mode === 'reply' ? 'Reply sent' : 'Forwarded');
      onSent && onSent();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Send failed');
    } finally { setSending(false); }
  };

  if (!message) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (sending) return; if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="inbox-compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'reply' ? <Reply className="w-5 h-5 text-[#0E7490]" /> : <Forward className="w-5 h-5 text-[#0E7490]" />}
            {mode === 'reply' ? 'Reply to' : 'Forward'} · {message.from_email}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {mode === 'forward' && (
            <div>
              <Label className="text-xs text-[#64748B]">To</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@…" className="h-9 text-sm" data-testid="compose-to" />
            </div>
          )}
          <div>
            <Label className="text-xs text-[#64748B]">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 text-sm" data-testid="compose-subject" />
          </div>
          {mode === 'reply' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-[#64748B]">Cc</Label>
                <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="a@b.com" className="h-9 text-xs" data-testid="compose-cc" />
              </div>
              <div>
                <Label className="text-xs text-[#64748B]">Bcc</Label>
                <Input value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="hidden@b.com" className="h-9 text-xs" data-testid="compose-bcc" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs text-[#64748B]">Body</Label>
            <RichTextEditor value={html} onChange={setHtml} placeholder="Write your reply…" />
          </div>
          {mode === 'reply' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-[#64748B] flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Attachments</Label>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fileRef.current?.click()} data-testid="compose-attach-btn">
                  <Paperclip className="w-3.5 h-3.5 mr-1.5" /> Add files
                </Button>
                <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { setAttachments([...attachments, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
              </div>
              {attachments.length > 0 && (
                <ul className="space-y-1">
                  {attachments.map((f, i) => (
                    <li key={i} className="flex items-center justify-between text-xs bg-[#F8FAFC] border border-[#E2E8F0] rounded px-2 py-1.5">
                      <span className="truncate">{f.name} <span className="text-[#94A3B8]">· {(f.size / 1024).toFixed(0)} KB</span></span>
                      <button onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} className="text-[#991B1B] hover:bg-[#991B1B]/10 p-1 rounded"><X className="w-3 h-3" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1 border-t border-[#F1F5F9]">
            <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
            <Button onClick={send} disabled={sending} className="careiq-btn-primary" data-testid="compose-send-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sending ? 'Sending…' : (mode === 'reply' ? 'Send reply' : 'Forward')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MailboxSettingsDialog({ open, onClose, mailboxes, onChanged }) {
  const [creating, setCreating] = useState(false);
  const [newAddr, setNewAddr] = useState('');
  const [newKeywords, setNewKeywords] = useState('');
  // IMAP state
  const [imapAccounts, setImapAccounts] = useState([]);
  const [imapForm, setImapForm] = useState({ address: '', password: '', imap_host: 'p3plzcpnl505791.prod.phx3.secureserver.net', imap_port: 993, smtp_host: '', smtp_port: 465, folder: 'INBOX', sync_interval_minutes: 5, fetch_limit: 50 });
  const [imapCreating, setImapCreating] = useState(false);
  const [imapBusyId, setImapBusyId] = useState(null);

  const loadImap = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/inbox/imap-accounts`, { headers: auth() });
      setImapAccounts(r.data.accounts || []);
    } catch { /* not super_admin */ }
  };
  useEffect(() => { if (open) loadImap(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  const create = async () => {
    if (!newAddr.trim()) return;
    setCreating(true);
    try {
      const payload = { address: newAddr.trim().toLowerCase() };
      if (newKeywords.trim()) payload.auto_ticket_keywords = newKeywords.split(',').map(s => s.trim()).filter(Boolean);
      await axios.post(`${API_URL}/api/inbox/mailboxes`, payload, { headers: auth() });
      toast.success('Mailbox created');
      setNewAddr(''); setNewKeywords('');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  const toggleAuto = async (mb) => {
    try {
      await axios.put(`${API_URL}/api/inbox/mailboxes/${mb.id}`, { auto_ticket_enabled: !mb.auto_ticket_enabled }, { headers: auth() });
      onChanged && onChanged();
    } catch { toast.error('Update failed'); }
  };
  const delMb = async (mb) => {
    if (!window.confirm(`Remove ${mb.address}? Existing messages stay.`)) return;
    try {
      await axios.delete(`${API_URL}/api/inbox/mailboxes/${mb.id}`, { headers: auth() });
      onChanged && onChanged();
    } catch { toast.error('Delete failed'); }
  };

  const createImap = async () => {
    if (!imapForm.address.trim() || !imapForm.password || !imapForm.imap_host) {
      toast.error('Email, password, and IMAP host are required'); return;
    }
    setImapCreating(true);
    try {
      const payload = { ...imapForm, address: imapForm.address.trim().toLowerCase() };
      await axios.post(`${API_URL}/api/inbox/imap-accounts`, payload, { headers: auth() });
      toast.success('IMAP account added — syncing now…');
      setImapForm({ ...imapForm, address: '', password: '' });
      await loadImap();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add account');
    } finally { setImapCreating(false); }
  };
  const testImap = async (acc) => {
    setImapBusyId(acc.id);
    try {
      const r = await axios.post(`${API_URL}/api/inbox/imap-accounts/${acc.id}/test`, {}, { headers: auth() });
      if (r.data.ok) toast.success(`Connected · ${r.data.messages_in_folder} messages in ${acc.folder || 'INBOX'}`);
      else toast.error('Test failed: ' + r.data.error);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    finally { setImapBusyId(null); }
  };
  const syncImap = async (acc) => {
    setImapBusyId(acc.id);
    try {
      const r = await axios.post(`${API_URL}/api/inbox/imap-accounts/${acc.id}/sync`, {}, { headers: auth() });
      if (r.data.error) toast.error('Sync error: ' + r.data.error);
      else toast.success(`Synced · ${r.data.inserted} new message${r.data.inserted === 1 ? '' : 's'}`);
      await loadImap();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setImapBusyId(null); }
  };
  const delImap = async (acc) => {
    if (!window.confirm(`Remove IMAP account for ${acc.address}? Existing messages stay.`)) return;
    try {
      await axios.delete(`${API_URL}/api/inbox/imap-accounts/${acc.id}`, { headers: auth() });
      await loadImap();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="mailbox-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-[#0E7490]" /> Mailboxes</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            {mailboxes.map(mb => (
              <div key={mb.id} className="border border-[#E2E8F0] rounded p-2 text-xs" data-testid={`mailbox-row-${mb.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-[#0A2342] truncate">{mb.address}</div>
                    <div className="text-[10px] text-[#94A3B8]">roles: {(mb.allowed_roles || []).join(', ')}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => toggleAuto(mb)} className={`text-[10px] px-2 py-1 rounded ${mb.auto_ticket_enabled ? 'bg-[#0E7490]/10 text-[#0E7490]' : 'bg-[#F1F5F9] text-[#64748B]'}`} data-testid={`mailbox-toggle-auto-${mb.id}`}>
                      Auto-ticket {mb.auto_ticket_enabled ? 'ON' : 'OFF'}
                    </button>
                    <button onClick={() => delMb(mb)} className="text-[#991B1B] p-1 hover:bg-[#991B1B]/5 rounded" data-testid={`mailbox-delete-${mb.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {mb.auto_ticket_enabled && (
                  <div className="text-[10px] text-[#64748B] mt-1">keywords: {mb.auto_ticket_keywords.join(', ')}</div>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-[#F1F5F9] pt-3">
            <Label className="text-xs text-[#64748B]">Add a mailbox</Label>
            <div className="flex gap-2 mt-1">
              <Input placeholder="hello@savenes.com" value={newAddr} onChange={(e) => setNewAddr(e.target.value)} className="h-8 text-xs" data-testid="new-mailbox-addr" />
              <Input placeholder="auto-ticket keywords (csv)" value={newKeywords} onChange={(e) => setNewKeywords(e.target.value)} className="h-8 text-xs" data-testid="new-mailbox-kw" />
              <Button size="sm" onClick={create} disabled={creating || !newAddr.trim()} className="careiq-btn-primary text-xs h-8" data-testid="new-mailbox-create">{creating ? '…' : 'Add'}</Button>
            </div>
          </div>

          <div className="border-t border-[#F1F5F9] pt-3 text-[11px] text-[#64748B] leading-snug bg-[#F8FAFC] rounded p-3">
            <div className="font-medium text-[#0A2342] mb-1">Resend setup</div>
            <ol className="list-decimal list-inside space-y-1">
              <li>In your Resend dashboard, open <b>Domains</b> → verify <code>savenes.com</code> (TXT + DKIM records).</li>
              <li>Open <b>Inbound</b> (or <b>Domains → Inbound emails</b>). If you don't see it, request access — it's still being rolled out.</li>
              <li>Add the <b>MX records</b> Resend gives you to your DNS (typically <code>feedback-smtp.resend.com</code>).</li>
              <li>Set the <b>inbound webhook URL</b> to: <code>https://savenes.com/api/webhooks/inbound-email</code></li>
              <li>Test by sending an email to <code>info@savenes.com</code>. It should appear here within a few seconds.</li>
            </ol>
            <div className="mt-2 text-[10px]">If Resend Inbound isn't available, you can route via Mailgun / Postmark / SES — the same webhook URL accepts their payloads.</div>
          </div>

          {/* IMAP accounts — pull from your existing inbox (GoDaddy, etc.) */}
          <div className="border-t border-[#F1F5F9] pt-3" data-testid="imap-accounts-section">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-[#0A2342]">IMAP mail accounts</div>
              <span className="text-[10px] text-[#94A3B8]">Pulls every {imapAccounts[0]?.sync_interval_minutes || 5} min</span>
            </div>
            {imapAccounts.length === 0 ? (
              <p className="text-[11px] text-[#94A3B8] mb-2">No IMAP accounts. Add one below — credentials are encrypted at rest with the platform's secret key.</p>
            ) : (
              <ul className="space-y-2 mb-3">
                {imapAccounts.map(acc => (
                  <li key={acc.id} className="border border-[#E2E8F0] rounded p-2 text-xs" data-testid={`imap-row-${acc.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-medium text-[#0A2342] truncate">{acc.address}</div>
                        <div className="text-[10px] text-[#94A3B8]">
                          {acc.imap_host}:{acc.imap_port}{acc.use_ssl ? ' · SSL' : ''} · folder {acc.folder}
                        </div>
                        <div className="text-[10px] text-[#94A3B8]">
                          synced {acc.messages_synced_total || 0}
                          {acc.last_sync_at ? ` · last ${fmtShort(acc.last_sync_at)}` : ' · never'}
                          {acc.last_sync_status === 'error' && <span className="text-[#991B1B]"> · error: {acc.last_sync_error}</span>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 shrink-0">
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={imapBusyId === acc.id} onClick={() => testImap(acc)} data-testid={`imap-test-${acc.id}`}>
                          {imapBusyId === acc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Test'}
                        </Button>
                        <Button size="sm" className="careiq-btn-primary h-7 text-xs" disabled={imapBusyId === acc.id} onClick={() => syncImap(acc)} data-testid={`imap-sync-${acc.id}`}>
                          {imapBusyId === acc.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Sync
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs text-[#991B1B]" onClick={() => delImap(acc)} data-testid={`imap-delete-${acc.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded p-2.5 space-y-2">
              <div className="text-[11px] font-medium text-[#0A2342]">Add account</div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="info@savenes.com" value={imapForm.address} onChange={(e) => setImapForm({ ...imapForm, address: e.target.value })} className="h-8 text-xs" data-testid="imap-form-address" />
                <Input type="password" placeholder="Mailbox password" value={imapForm.password} onChange={(e) => setImapForm({ ...imapForm, password: e.target.value })} className="h-8 text-xs" data-testid="imap-form-password" />
                <Input placeholder="IMAP host" value={imapForm.imap_host} onChange={(e) => setImapForm({ ...imapForm, imap_host: e.target.value })} className="h-8 text-xs col-span-2" data-testid="imap-form-host" />
                <Input type="number" placeholder="IMAP port" value={imapForm.imap_port} onChange={(e) => setImapForm({ ...imapForm, imap_port: +e.target.value || 993 })} className="h-8 text-xs" data-testid="imap-form-port" />
                <Input placeholder="Folder (INBOX)" value={imapForm.folder} onChange={(e) => setImapForm({ ...imapForm, folder: e.target.value })} className="h-8 text-xs" data-testid="imap-form-folder" />
                <Input type="number" placeholder="Sync every (min)" value={imapForm.sync_interval_minutes} onChange={(e) => setImapForm({ ...imapForm, sync_interval_minutes: +e.target.value || 5 })} className="h-8 text-xs" />
                <Input type="number" placeholder="Fetch limit" value={imapForm.fetch_limit} onChange={(e) => setImapForm({ ...imapForm, fetch_limit: +e.target.value || 50 })} className="h-8 text-xs" />
              </div>
              <div className="flex justify-end">
                <Button size="sm" className="careiq-btn-primary text-xs h-8" disabled={imapCreating} onClick={createImap} data-testid="imap-form-create">
                  {imapCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                  Add account
                </Button>
              </div>
            </div>
            <div className="mt-3 text-[10px] text-[#94A3B8] leading-snug bg-[#FFFBEB] border border-[#FCD34D] rounded p-2">
              <b className="text-[#92400E]">GoDaddy / cPanel preset</b> — host <code>p3plzcpnl505791.prod.phx3.secureserver.net</code> · port 993 (SSL) · username = full email (e.g. <code>info@savenes.com</code>) · password = mailbox password. <b>If 2FA is enabled, generate an app password.</b>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
